import { designAreaProblems, newDesignLayerId, resolveDesignAreaConfig, type DesignAreaAnswer, type DesignAreaConfig, type DesignLayer } from './design-area.js';
import { assertEditable, assertRemovable } from './designer-parity.js';
import { STICKER_DEFAULTS } from './sticker.js';

export const DESIGNER_LAYOUTS = [
  { id: 'image-only', name: 'Samo slika' },
  { id: 'text-only', name: 'Samo tekst' },
  { id: 'text-above', name: 'Tekst iznad slike' },
  { id: 'text-below', name: 'Tekst ispod slike' },
  { id: 'text-overlay', name: 'Tekst preko slike' },
] as const;
export type DesignerLayoutMode = typeof DESIGNER_LAYOUTS[number]['id'];
export interface DesignerLayoutCommand {
  op: 'starterLayout';
  mode: DesignerLayoutMode;
  fontId: string;
  imageLayerId?: string;
  textLayerId?: string;
  text?: string;
  gapMm?: number;
  sticker?: boolean;
  authoring?: boolean;
}

/** Atomic composition shortcut; uses existing native image/text contracts. */
export function applyDesignerLayout(config: DesignAreaConfig, answer: DesignAreaAnswer, command: DesignerLayoutCommand): DesignAreaAnswer {
  assertRemovable(answer.layers, command.authoring);
  const cfg = resolveDesignAreaConfig(config);
  const wantsImage = command.mode !== 'text-only', wantsText = command.mode !== 'image-only';
  const sourceImage = command.imageLayerId
    ? answer.layers.find(l => l.id === command.imageLayerId && l.kind === 'image')
    : answer.layers.find(l => l.kind === 'image' && l.visible !== false);
  const sourceText = command.textLayerId
    ? answer.layers.find(l => l.id === command.textLayerId && l.kind === 'text')
    : answer.layers.find(l => l.kind === 'text' && l.visible !== false);
  if (command.imageLayerId && !sourceImage) throw new Error('The selected photo no longer exists');
  if (command.textLayerId && !sourceText) throw new Error('The selected text no longer exists');
  if (wantsImage && !sourceImage) throw new Error('Prvo dodajte sliku, pa izaberite raspored.');
  const selected = [wantsImage ? sourceImage : undefined, wantsText ? sourceText : undefined].filter((l): l is DesignLayer => !!l);
  assertEditable(selected, command.authoring);
  if (selected.some(l => l.locked)) throw new Error('Unlock selected layers before editing them');
  const sticker = command.sticker === false ? undefined : answer.sticker ?? (command.sticker || config.sticker?.required ? config.sticker?.defaults ?? STICKER_DEFAULTS : undefined);
  if (command.sticker === false && config.sticker?.required) throw new Error('This product requires Sticker');
  if (sticker && !config.sticker?.allowed) throw new Error('Sticker is not available for this product');
  const margin = Math.max(cfg.safeMarginMm, 4, sticker ? sticker.borderMm + 2 : 0);
  const width = cfg.sizeMm.w - 2 * margin, height = cfg.sizeMm.h - 2 * margin;
  const gap = command.gapMm ?? 3;
  if (width <= 0 || height <= 0) throw new Error('Print zone is too small for this layout');
  const stacked = command.mode === 'text-above' || command.mode === 'text-below';
  const textHeight = wantsImage ? Math.min(height * .26, 22) : height;
  const imageHeight = stacked ? height - textHeight - gap : height;
  if (wantsImage && imageHeight <= 0) throw new Error('Reduce the gap to fit this print zone');
  const layers: DesignLayer[] = [];
  if (wantsImage && sourceImage?.kind === 'image') {
    const photo = structuredClone(sourceImage);
    photo.id = newDesignLayerId(); delete photo.groupId; delete photo.pattern;
    photo.visible = true;
    photo.box = { x: cfg.sizeMm.w / 2, y: stacked ? margin + (command.mode === 'text-above' ? textHeight + gap : 0) + imageHeight / 2 : cfg.sizeMm.h / 2, w: width, h: imageHeight };
    if (!photo.crop) photo.fit = 'contain';
    layers.push(photo);
  }
  if (wantsText) {
    const value = command.text ?? (sourceText?.kind === 'text' ? sourceText.text : cfg.text.defaultText);
    const lines = value.split('\n');
    const lineSpacing = sourceText?.kind === 'text' ? sourceText.lineSpacing ?? 1.15 : 1.15;
    const size = Math.min(cfg.text.maxSizeMm, textHeight * .8 / (Math.max(1, lines.length) * lineSpacing), textHeight * .45, width / Math.max(4, ...lines.map(line => Array.from(line).length)) * 1.4);
    if (size < cfg.text.minSizeMm) throw new Error('Shorten the text or use a larger print zone');
    const text: Extract<DesignLayer, {kind:'text'}> = sourceText?.kind === 'text' ? structuredClone(sourceText) : {
      id: newDesignLayerId(), kind: 'text', text: value, fontId: command.fontId,
      color: cfg.text.palette[0]!, sizeMm: size, box: {x:0,y:0,w:1,h:1},
    };
    text.id = newDesignLayerId(); delete text.groupId; delete text.pattern; delete text.bend; delete text.textWidthMm;
    text.visible = true; text.text = value; text.sizeMm = size; text.orientation = 'horizontal';
    text.align = 'center'; text.boxMode = 'fixed'; text.anchorV = 'middle';
    text.box = { x: cfg.sizeMm.w / 2, y: command.mode === 'text-above' ? margin + textHeight / 2 : command.mode === 'text-below' ? cfg.sizeMm.h - margin - textHeight / 2 : cfg.sizeMm.h / 2, w: width, h: textHeight };
    layers.push(text);
  }
  const next: DesignAreaAnswer = { v: 1, layers, ...(sticker ? {sticker:structuredClone(sticker)} : {}) };
  const problems = designAreaProblems(config, next);
  if (problems.length) throw new Error(problems[0]!.detail);
  return next;
}
